import { supabase } from '@/lib/supabase';

export interface CatalogListing {
  id: string;
  listing_type: 'laptop_group' | 'accessory';
  slug: string;
  title: string;
  description: string | null;
  cover_image_path: string | null;
  image_paths: string[];
  is_featured: boolean;
  is_published: boolean;
  show_out_of_stock: boolean;
  sort_order: number;
  brand: string | null;
  model: string | null;
  cpu: string | null;
  touchscreen: boolean | null;
  accessory_id: string | null;
  display_price_override: number | null;
  original_price: number | null;
  badge: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface PublicCatalogListing {
  id: string;
  listing_type: 'laptop_group' | 'accessory';
  slug: string;
  title: string;
  description: string | null;
  cover_image_path: string | null;
  image_paths: string[];
  is_featured: boolean;
  sort_order: number;
  brand: string | null;
  model: string | null;
  cpu: string | null;
  touchscreen: boolean | null;
  available_quantity: number;
  min_price: number | null;
  max_price: number | null;
  ram_variants: string[];
  storage_variants: string[];
  gpu_variants: string[];
  condition_summary: string[];
}

export interface CatalogListingInput {
  listing_type: 'laptop_group' | 'accessory';
  slug: string;
  title: string;
  description?: string | null;
  cover_image_path?: string | null;
  image_paths?: string[];
  is_featured?: boolean;
  is_published?: boolean;
  show_out_of_stock?: boolean;
  sort_order?: number;
  brand?: string | null;
  model?: string | null;
  cpu?: string | null;
  touchscreen?: boolean | null;
  accessory_id?: string | null;
  display_price_override?: number | null;
  original_price?: number | null;
  badge?: string | null;
}

export async function fetchCatalogListings(): Promise<CatalogListing[]> {
  const { data, error } = await supabase
    .from('catalog_listings')
    .select('*')
    .order('is_featured', { ascending: false })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as CatalogListing[];
}

export async function createCatalogListing(payload: CatalogListingInput): Promise<CatalogListing> {
  const { data, error } = await supabase
    .from('catalog_listings')
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data as CatalogListing;
}

export async function updateCatalogListing(
  id: string,
  payload: Partial<CatalogListingInput>
): Promise<CatalogListing> {
  const { data, error } = await supabase
    .from('catalog_listings')
    .update({ ...payload, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CatalogListing;
}

export async function deleteCatalogListing(id: string): Promise<void> {
  const { error } = await supabase
    .from('catalog_listings')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function fetchPublicCatalogListings(): Promise<PublicCatalogListing[]> {
  const { data, error } = await supabase.rpc('get_public_catalog_listings');
  if (error) throw error;
  return (data ?? []) as PublicCatalogListing[];
}

export async function fetchPublicCatalogListingBySlug(slug: string): Promise<PublicCatalogListing | null> {
  const listings = await fetchPublicCatalogListings();
  const match = listings.find((l) => l.slug.toLowerCase() === slug.toLowerCase());
  return match || null;
}
